// Vorsorge-Tab der Finanzplanung (Paket 2): Rentenlücke (Angestellte) bzw.
// Pensionslücke (Beamte) — Portierung der Sheets 10a/10b aus „Mandanten-Log
// II". Der Server speichert nur die Eingaben (finance_calculators.inputs),
// gerechnet wird live mit der Shared-Engine; Blur-Autosave pro Feld.
import {
  BESOLDUNGSTAFEL_2021,
  besoldungFor,
  CIVIL_PENSION_DEFAULT_INPUTS,
  CIVIL_PENSION_DEFAULT_PARAMS,
  type CivilPensionInputs,
  type CivilPensionParams,
  computeCivilPensionGap,
  computePensionGap,
  FINANCE_MAX_MONTHLY_CENTS,
  PENSION_DEFAULT_INPUTS,
  PENSION_DEFAULT_PARAMS,
  PENSION_RENTENFAKTOR_OST_CENTS,
  PENSION_RENTENFAKTOR_WEST_CENTS,
  type PensionInputs,
  type PensionParams,
  VORSORGE_BOUNDS,
} from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { api, type FinanceCalculatorKind } from '../../lib/api'
import { centsToInput, parseCents, parseDecimalInput } from '../../lib/finance-input'
import { formatCurrency, formatNumber } from '../../lib/format'
import { notify } from '../../lib/notify'
import { useThemeColors } from '../../lib/theme-colors'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Select } from '../ui/select'

type Mode = 'employee' | 'civil_servant'

interface PensionState extends PensionInputs {
  params?: Partial<PensionParams>
}
interface CivilState extends CivilPensionInputs {
  params?: Partial<CivilPensionParams>
}

/** Client-Clamp auf die geteilten Server-Grenzen — verhindert 400er, die der
 *  User nicht versteht (Review-Fund: Client kannte die Zod-Bounds nicht). */
function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi)
}

export function VorsorgeTab({ viewUserId, readOnly }: { viewUserId: string; readOnly: boolean }) {
  const qc = useQueryClient()
  const [mode, setMode] = useState<Mode>(() =>
    localStorage.getItem('finanz-vorsorge-mode') === 'civil_servant' ? 'civil_servant' : 'employee',
  )
  // Erzwingt nach einem Server-400 den Remount der Rechner (Review-Fund:
  // ohne Reset bliebe der abgelehnte Wert im State und JEDER Folge-Save
  // desselben kompletten inputs-Objekts liefe erneut in 400).
  const [resetNonce, setResetNonce] = useState(0)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['finance', 'calculators', viewUserId || 'me'],
    queryFn: () => api.getFinanceCalculators(viewUserId || undefined),
  })

  const save = useMutation({
    mutationFn: ({
      kind,
      inputs,
    }: {
      kind: FinanceCalculatorKind
      inputs: Record<string, unknown>
    }) => api.saveFinanceCalculator(kind, inputs),
    // Serieller Scope: parallele Blur-PUTs könnten sich sonst überholen und
    // ein älterer Voll-Payload überschriebe den jüngeren (Review-Fund).
    scope: { id: 'finance-calculator-save' },
    // Kein Erfolgs-Toast (Blur-Autosave).
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance', 'calculators'] }),
    onError: (e: Error) => {
      notify.error(e.message)
      // Refetch allein erreicht die Rechner-States nicht (Initializer-only)
      // — Nonce im key remountet auf den letzten gültigen Server-Stand.
      setResetNonce((n) => n + 1)
      qc.invalidateQueries({ queryKey: ['finance', 'calculators'] })
    },
  })

  if (isLoading) return <p className="text-text-secondary">Lade …</p>
  if (isError)
    return (
      <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
        <p className="text-text-secondary">Vorsorge-Daten konnten nicht geladen werden.</p>
        <Button className="mt-4" variant="secondary" onClick={() => refetch()}>
          Erneut versuchen
        </Button>
      </div>
    )

  const rows = data?.calculators ?? []
  const pensionRow = rows.find((r) => r.kind === 'pension')
  const civilRow = rows.find((r) => r.kind === 'civil_pension')

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 rounded-xl bg-bg-overlay/40 p-1 w-fit">
        {(
          [
            { key: 'employee', label: 'Angestellt' },
            { key: 'civil_servant', label: 'Beamt:in' },
          ] as Array<{ key: Mode; label: string }>
        ).map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => {
              // iOS tippt Toggle ohne vorheriges Blur — aktives Feld synchron
              // blurren, damit dessen Commit VOR dem Moduswechsel läuft.
              blurActiveField()
              setMode(m.key)
              localStorage.setItem('finanz-vorsorge-mode', m.key)
            }}
            className={cn(
              'rounded-lg px-3.5 py-1.5 text-sm transition-colors touch:min-h-11',
              mode === m.key
                ? 'bg-bg-elevated text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary',
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      {readOnly && !(mode === 'employee' ? pensionRow : civilRow) ? (
        // Admin-Sicht ohne gespeicherte Daten: Defaults sähen sonst wie
        // Nutzereingaben aus (Review-Fund) — expliziter Leer-Zustand.
        <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
          <p className="text-text-secondary">
            Für diesen Rechner wurden noch keine Daten eingetragen.
          </p>
        </div>
      ) : mode === 'employee' ? (
        <PensionCalculator
          key={`p-${viewUserId}-${resetNonce}`}
          serverInputs={pensionRow?.inputs}
          serverUpdatedAt={pensionRow?.updatedAt}
          readOnly={readOnly}
          onSave={(inputs) => save.mutate({ kind: 'pension', inputs })}
        />
      ) : (
        <CivilCalculator
          key={`c-${viewUserId}-${resetNonce}`}
          serverInputs={civilRow?.inputs}
          serverUpdatedAt={civilRow?.updatedAt}
          readOnly={readOnly}
          onSave={(inputs) => save.mutate({ kind: 'civil_pension', inputs })}
        />
      )}
    </div>
  )
}

/** Aktives Eingabefeld synchron blurren — natives blur() feuert Reacts
 *  onBlur sofort, der Commit läuft also VOR dem auslösenden Klick-Handler. */
function blurActiveField() {
  const el = document.activeElement
  if (el instanceof HTMLElement) el.blur()
}

// === Angestellte: Rentenlücke ===============================================

function PensionCalculator({
  serverInputs,
  serverUpdatedAt,
  readOnly,
  onSave,
}: {
  serverInputs: Record<string, unknown> | undefined
  serverUpdatedAt: string | undefined
  readOnly: boolean
  onSave: (inputs: Record<string, unknown>) => void
}) {
  const colors = useThemeColors()
  const [state, setState] = useState<PensionState>(() => ({
    ...PENSION_DEFAULT_INPUTS,
    ...(serverInputs as Partial<PensionState> | undefined),
  }))
  const stateRef = useRef(state)
  stateRef.current = state

  // Server-Sync nach Mount (Review-Fund: Initializer-only ließ die Admin-
  // Sicht veralten): nur bei NEUEM updatedAt und echt anderem Inhalt —
  // eigene Saves erzeugen denselben Inhalt und bleiben so no-ops.
  const seenUpdatedAt = useRef(serverUpdatedAt)
  useEffect(() => {
    if (!serverUpdatedAt || serverUpdatedAt === seenUpdatedAt.current) return
    seenUpdatedAt.current = serverUpdatedAt
    const next: PensionState = {
      ...PENSION_DEFAULT_INPUTS,
      ...(serverInputs as Partial<PensionState> | undefined),
    }
    if (JSON.stringify(next) !== JSON.stringify(stateRef.current)) {
      stateRef.current = next
      setState(next)
    }
  }, [serverUpdatedAt, serverInputs])

  const params: PensionParams = { ...PENSION_DEFAULT_PARAMS, ...state.params }
  const result = computePensionGap(state, params)

  // Atomar: nächsten State synchron bauen, DANN setState + Save mit
  // demselben Objekt — setState ist async, ein nachgelagertes commit()
  // speicherte sonst den Vorzustand (Smoke-Test-Fund).
  function patch(p: Partial<PensionState>) {
    const next = { ...stateRef.current, ...p }
    stateRef.current = next
    setState(next)
    if (!readOnly) onSave(next as unknown as Record<string, unknown>)
  }
  function patchParams(p: Partial<PensionParams>) {
    patch({ params: { ...stateRef.current.params, ...p } })
  }

  const noPension = state.grossMonthlyCents === 0 && state.netMonthlyCents === 0

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Card title="Persönliche Daten">
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberField
              label="Alter"
              value={state.age}
              readOnly={readOnly}
              onCommit={(v) => {
                patch({ age: clamp(Math.round(v), VORSORGE_BOUNDS.ageMin, VORSORGE_BOUNDS.ageMax) })
              }}
            />
            <NumberField
              label="Bisherige Entgeltpunkte"
              value={state.pensionPoints}
              decimals
              readOnly={readOnly}
              onCommit={(v) => {
                patch({ pensionPoints: clamp(v, 0, VORSORGE_BOUNDS.pensionPointsMax) })
              }}
            />
            <EuroField
              label="Heutiges Netto (mtl.)"
              cents={state.netMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ netMonthlyCents: c })
              }}
            />
            <EuroField
              label="Heutiges Brutto (mtl.)"
              cents={state.grossMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ grossMonthlyCents: c })
              }}
            />
            <EuroField
              label="Vorhandene BU-Rente (mtl.)"
              cents={state.buPensionMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ buPensionMonthlyCents: c })
              }}
            />
            <EuroField
              label="Krankentagegeld (€/Tag)"
              cents={state.ktgDailyCents}
              maxCents={VORSORGE_BOUNDS.ktgDailyMaxCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ ktgDailyCents: c })
              }}
            />
            <EuroField
              label="Geförderte Rente (Riester/Rürup/BaV, mtl.)"
              cents={state.fundedPensionMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ fundedPensionMonthlyCents: c })
              }}
            />
            <EuroField
              label="Kapitalgebundene Rente (mtl.)"
              cents={state.capitalPensionMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ capitalPensionMonthlyCents: c })
              }}
            />
            <EuroField
              label="Immobilien-Miete (mtl.)"
              cents={state.rentalIncomeMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ rentalIncomeMonthlyCents: c })
              }}
            />
          </div>
        </Card>

        <Card title="Renten nach Kategorien (monatlich)">
          <ResultRow
            label={`Gesetzliche Rente (${formatNumber(result.totalPensionPoints, 2)} Entgeltpunkte)`}
            cents={result.statutoryPensionGrossCents}
          />
          <ResultRow label="+ Geförderte Rente" cents={state.fundedPensionMonthlyCents} />
          <ResultRow label="+ Kapitalgebundene Rente" cents={state.capitalPensionMonthlyCents} />
          <ResultRow label="+ Immobilien-Miete" cents={state.rentalIncomeMonthlyCents} />
          <ResultRow label="Rente brutto" cents={result.pensionGrossTotalCents} strong />
          <ResultRow
            label={`Rente netto (Abgabenfaktor ${formatNumber(result.taxFactor * 100, 1)} %)`}
            cents={result.pensionNetCents}
          />
          <ResultRow
            label="Kaufkraft nach Inflation"
            cents={result.purchasingPowerCents}
            tone="warning"
          />
          <GapRow label="Rentenlücke" cents={result.gapCents} />
        </Card>

        <Card title="Wenn der BU-Fall eintritt (monatlich)">
          <ResultRow
            label="Volle Erwerbsminderungsrente brutto"
            cents={result.emPensionGrossCents}
          />
          <ResultRow label="+ BU-Rente" cents={state.buPensionMonthlyCents} />
          <GapRow label="Lücke bei voller EM-Rente" cents={result.bu.fullEmGapCents} />
          <GapRow label="Lücke bei halber EM-Rente" cents={result.bu.halfEmGapCents} />
          <GapRow label="Lücke ohne EM-Rente" cents={result.bu.noneGapCents} />
        </Card>

        <Card title="Wenn Krankengeld bezogen wird (monatlich)">
          <ResultRow
            label="Gesetzliches Krankengeld netto"
            cents={result.sickPay.krankengeldNetCents}
          />
          <ResultRow
            label={`+ Krankentagegeld (${formatCurrency(state.ktgDailyCents)}/Tag)`}
            cents={state.ktgDailyCents * 30}
          />
          <GapRow label="Lücke" cents={result.sickPay.gapWithKtgCents} />
          <ResultRow
            label="Empfohlenes Krankentagegeld"
            cents={result.sickPay.recommendedKtgDailyCents}
            suffix="/Tag"
            strong
          />
        </Card>
      </div>

      <div className="space-y-4">
        <GapDonut
          title="Rentenlücke"
          slices={[
            {
              name: 'Kaufkraft Rente',
              value: Math.max(result.purchasingPowerCents, 0),
              color: colors.warning,
            },
            { name: 'Rentenlücke', value: Math.max(-result.gapCents, 0), color: colors.danger },
          ]}
          empty={noPension}
        />
        <GapDonut
          title="BU-Fall (halbe EM-Rente)"
          slices={[
            {
              name: 'Halbe EM-Rente',
              value: Math.max(Math.round(result.emPensionGrossCents / 2), 0),
              color: colors.success,
            },
            { name: 'BU-Rente', value: state.buPensionMonthlyCents, color: colors.info },
            { name: 'Lücke', value: Math.max(-result.bu.halfEmGapCents, 0), color: colors.danger },
          ]}
          empty={noPension}
        />
        <GapDonut
          title="Krankengeld"
          slices={[
            {
              name: 'Krankengeld netto',
              value: result.sickPay.krankengeldNetCents,
              color: colors.success,
            },
            { name: 'Krankentagegeld', value: state.ktgDailyCents * 30, color: colors.info },
            {
              name: 'Lücke',
              value: Math.max(-result.sickPay.gapWithKtgCents, 0),
              color: colors.danger,
            },
          ]}
          empty={noPension}
        />

        <Card title="Inflation & Kaufkraft">
          <ResultRow
            label={`Kaufkraft-Faktor (${result.yearsToRetirement} Jahre)`}
            text={`${formatNumber(result.inflationFactor * 100, 2)} %`}
          />
          <ResultRow label="Aus 100 € werden" cents={result.from100Cents} />
          <ResultRow label="Kaufkraft-Verlust" cents={result.purchasingLossCents} tone="danger" />
        </Card>

        <ParamsPanel legend="Rechen-Parameter (Stand 2021 — anpassbar)">
          <EuroField
            label="Rentenfaktor (€/Entgeltpunkt)"
            cents={params.rentenfaktorCents}
            readOnly={readOnly}
            onCommit={(c) => {
              patchParams({ rentenfaktorCents: c })
            }}
          />
          {!readOnly && (
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                className="text-accent hover:underline"
                onClick={() => {
                  patchParams({ rentenfaktorCents: PENSION_RENTENFAKTOR_WEST_CENTS })
                }}
              >
                West ({formatCurrency(PENSION_RENTENFAKTOR_WEST_CENTS)})
              </button>
              <button
                type="button"
                className="text-accent hover:underline"
                onClick={() => {
                  patchParams({ rentenfaktorCents: PENSION_RENTENFAKTOR_OST_CENTS })
                }}
              >
                Ost ({formatCurrency(PENSION_RENTENFAKTOR_OST_CENTS)})
              </button>
            </div>
          )}
          <EuroField
            label="ø-Jahreseinkommen (= 1 Entgeltpunkt)"
            cents={params.avgAnnualIncomeCents}
            readOnly={readOnly}
            onCommit={(c) => {
              patchParams({ avgAnnualIncomeCents: c })
            }}
          />
          <EuroField
            label="Beitragsbemessungsgrenze (Jahr)"
            cents={params.bbgAnnualCents}
            readOnly={readOnly}
            onCommit={(c) => {
              patchParams({ bbgAnnualCents: c })
            }}
          />
          <EuroField
            label="KV-Beitragsbemessungsgrenze (Monat)"
            cents={params.bbgKvMonthlyCents}
            readOnly={readOnly}
            onCommit={(c) => {
              patchParams({ bbgKvMonthlyCents: c })
            }}
          />
          <NumberField
            label="Inflation p.a. (%)"
            value={params.inflationRate * 100}
            decimals
            allowNegative
            readOnly={readOnly}
            onCommit={(v) => {
              patchParams({
                inflationRate:
                  clamp(
                    v,
                    -VORSORGE_BOUNDS.inflationAbs * 100,
                    VORSORGE_BOUNDS.inflationAbs * 100,
                  ) / 100,
              })
            }}
          />
          <NumberField
            label="Renteneintrittsalter"
            value={params.retirementAge}
            readOnly={readOnly}
            onCommit={(v) => {
              patchParams({
                retirementAge: clamp(
                  Math.round(v),
                  VORSORGE_BOUNDS.retirementMin,
                  VORSORGE_BOUNDS.retirementMax,
                ),
              })
            }}
          />
        </ParamsPanel>
      </div>
    </div>
  )
}

// === Beamte: Pensionslücke ==================================================

function CivilCalculator({
  serverInputs,
  serverUpdatedAt,
  readOnly,
  onSave,
}: {
  serverInputs: Record<string, unknown> | undefined
  serverUpdatedAt: string | undefined
  readOnly: boolean
  onSave: (inputs: Record<string, unknown>) => void
}) {
  const colors = useThemeColors()
  const [state, setState] = useState<CivilState>(() => ({
    ...CIVIL_PENSION_DEFAULT_INPUTS,
    ...(serverInputs as Partial<CivilState> | undefined),
  }))
  const stateRef = useRef(state)
  stateRef.current = state

  // Server-Sync wie im PensionCalculator (siehe Kommentar dort).
  const seenUpdatedAt = useRef(serverUpdatedAt)
  useEffect(() => {
    if (!serverUpdatedAt || serverUpdatedAt === seenUpdatedAt.current) return
    seenUpdatedAt.current = serverUpdatedAt
    const next: CivilState = {
      ...CIVIL_PENSION_DEFAULT_INPUTS,
      ...(serverInputs as Partial<CivilState> | undefined),
    }
    if (JSON.stringify(next) !== JSON.stringify(stateRef.current)) {
      stateRef.current = next
      setState(next)
    }
  }, [serverUpdatedAt, serverInputs])

  const params: CivilPensionParams = { ...CIVIL_PENSION_DEFAULT_PARAMS, ...state.params }
  const result = computeCivilPensionGap(state, params)
  const tafelCents = besoldungFor(state.besoldungsGroup, state.besoldungsStufe)

  // Atomar wie im PensionCalculator (siehe Kommentar dort).
  function patch(p: Partial<CivilState>) {
    const next = { ...stateRef.current, ...p }
    stateRef.current = next
    setState(next)
    if (!readOnly) onSave(next as unknown as Record<string, unknown>)
  }
  function patchParams(p: Partial<CivilPensionParams>) {
    patch({ params: { ...stateRef.current.params, ...p } })
  }

  const empty = state.futureAvgGrossMonthlyCents === 0 && state.netMonthlyCents === 0

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Card title="Persönliche Daten">
          <p className="mb-2 text-xs text-text-tertiary">
            Mindestens 5 Jahre Beamter auf Lebenszeit, sonst kein Anspruch.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <span className="text-xs text-text-secondary">Besoldungsgruppe / Stufe</span>
              <div className="flex items-center gap-2">
                <Select
                  value={state.besoldungsGroup}
                  disabled={readOnly}
                  onChange={(e) => {
                    patch({ besoldungsGroup: e.target.value })
                  }}
                  className="h-9 w-24 text-sm"
                  aria-label="Besoldungsgruppe"
                >
                  {BESOLDUNGSTAFEL_2021.map((r) => (
                    <option key={r.group} value={r.group}>
                      {r.group}
                    </option>
                  ))}
                </Select>
                <Select
                  value={String(state.besoldungsStufe)}
                  disabled={readOnly}
                  onChange={(e) => {
                    patch({ besoldungsStufe: Number(e.target.value) })
                  }}
                  className="h-9 w-20 text-sm"
                  aria-label="Stufe"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </div>
              {tafelCents !== null ? (
                <div className="flex items-center gap-2 text-xs text-text-tertiary">
                  <span className="font-mono tabular-nums">
                    Tafel: {formatCurrency(tafelCents)}
                  </span>
                  {!readOnly && (
                    <button
                      type="button"
                      className="text-accent hover:underline"
                      onClick={() => {
                        patch({ grossMonthlyCents: tafelCents })
                      }}
                    >
                      als Brutto übernehmen
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-text-tertiary">Stufe außerhalb der Tafel</p>
              )}
            </div>
            <NumberField
              label="Alter"
              value={state.age}
              readOnly={readOnly}
              onCommit={(v) => {
                patch({ age: clamp(Math.round(v), VORSORGE_BOUNDS.ageMin, VORSORGE_BOUNDS.ageMax) })
              }}
            />
            <EuroField
              label="Heutiges Netto (mtl.)"
              cents={state.netMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ netMonthlyCents: c })
              }}
            />
            <EuroField
              label="Heutiges Brutto (mtl.)"
              cents={state.grossMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ grossMonthlyCents: c })
              }}
            />
            <NumberField
              label="Ruhegehaltsfähige Dienstjahre"
              value={state.serviceYears}
              decimals
              readOnly={readOnly}
              onCommit={(v) => {
                patch({ serviceYears: clamp(v, 0, VORSORGE_BOUNDS.serviceYearsMax) })
              }}
            />
            <EuroField
              label="Zukünftiges ø-Brutto der letzten 5 Dienstjahre"
              cents={state.futureAvgGrossMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ futureAvgGrossMonthlyCents: c })
              }}
            />
            <EuroField
              label="Vorhandene DU-Rente (mtl.)"
              cents={state.duPensionMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ duPensionMonthlyCents: c })
              }}
            />
            <EuroField
              label="Geförderte Rente (mtl.)"
              cents={state.fundedPensionMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ fundedPensionMonthlyCents: c })
              }}
            />
            <EuroField
              label="Kapitalgebundene Rente (mtl.)"
              cents={state.capitalPensionMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ capitalPensionMonthlyCents: c })
              }}
            />
            <EuroField
              label="Immobilien-Miete (mtl.)"
              cents={state.rentalIncomeMonthlyCents}
              readOnly={readOnly}
              onCommit={(c) => {
                patch({ rentalIncomeMonthlyCents: c })
              }}
            />
          </div>
        </Card>

        <Card title="Pension nach Kategorien (monatlich)">
          <ResultRow label="~ Zukünftiges Netto" cents={result.futureNetMonthlyCents} />
          <ResultRow label="Beamten-Pension brutto" cents={result.pensionGrossCents} />
          <ResultRow
            label="+ Geförderte / kapitalgebundene Rente + Miete"
            cents={
              state.fundedPensionMonthlyCents +
              state.capitalPensionMonthlyCents +
              state.rentalIncomeMonthlyCents
            }
          />
          <ResultRow label="Pension brutto gesamt" cents={result.pensionGrossTotalCents} strong />
          <ResultRow
            label={`Pension netto (Abgabenfaktor ${formatNumber(result.taxFactor * 100, 1)} %)`}
            cents={result.pensionNetCents}
          />
          <ResultRow
            label="Kaufkraft nach Inflation"
            cents={result.purchasingPowerCents}
            tone="warning"
          />
          <GapRow label="Pensionslücke" cents={result.gapCents} />
        </Card>

        <Card title="Wenn der DU-Fall eintritt (monatlich)">
          <ResultRow label="Ruhegehalt brutto" cents={result.du.ruhegehaltGrossCents} />
          <ResultRow label="+ DU-Rente" cents={state.duPensionMonthlyCents} />
          <GapRow label="Lücke bei Ruhegehalt" cents={result.du.gapCents} />
          <GapRow label="Zukünftiger Besoldungsausfall" cents={result.du.besoldungsausfallCents} />
        </Card>
      </div>

      <div className="space-y-4">
        <GapDonut
          title="Pensionslücke"
          slices={[
            {
              name: 'Kaufkraft Pension',
              value: Math.max(result.purchasingPowerCents, 0),
              color: colors.warning,
            },
            { name: 'Pensionslücke', value: Math.max(-result.gapCents, 0), color: colors.danger },
          ]}
          empty={empty}
        />
        <GapDonut
          title="DU-Fall"
          slices={[
            {
              name: 'Ruhegehalt brutto',
              value: result.du.ruhegehaltGrossCents,
              color: colors.success,
            },
            { name: 'DU-Rente', value: state.duPensionMonthlyCents, color: colors.info },
            { name: 'Lücke', value: Math.max(-result.du.gapCents, 0), color: colors.danger },
            {
              name: 'Zukünftiger Besoldungsausfall',
              value: Math.max(-result.du.besoldungsausfallCents, 0),
              color: colors.warning,
            },
          ]}
          empty={empty}
        />

        <Card title="Inflation & Kaufkraft">
          <ResultRow
            label={`Kaufkraft-Faktor (${result.yearsToRetirement} Jahre)`}
            text={`${formatNumber(result.inflationFactor * 100, 2)} %`}
          />
          <ResultRow label="Aus 100 € werden" cents={result.from100Cents} />
          <ResultRow label="Kaufkraft-Verlust" cents={result.purchasingLossCents} tone="danger" />
        </Card>

        <ParamsPanel legend="Rechen-Parameter (Stand 2021 — anpassbar)">
          <NumberField
            label="Inflation abzügl. Besoldungserhöhung (%)"
            value={params.inflationRate * 100}
            decimals
            allowNegative
            readOnly={readOnly}
            onCommit={(v) => {
              patchParams({
                inflationRate:
                  clamp(
                    v,
                    -VORSORGE_BOUNDS.inflationAbs * 100,
                    VORSORGE_BOUNDS.inflationAbs * 100,
                  ) / 100,
              })
            }}
          />
          <NumberField
            label="Pensionseintrittsalter"
            value={params.retirementAge}
            readOnly={readOnly}
            onCommit={(v) => {
              patchParams({
                retirementAge: clamp(
                  Math.round(v),
                  VORSORGE_BOUNDS.retirementMin,
                  VORSORGE_BOUNDS.retirementMax,
                ),
              })
            }}
          />
          <EuroField
            label="Mindestversorgung (mtl.)"
            cents={params.minPensionMonthlyCents}
            readOnly={readOnly}
            onCommit={(c) => {
              patchParams({ minPensionMonthlyCents: c })
            }}
          />
          <NumberField
            label="Netto-Quote (~)"
            value={params.netRatio}
            decimals
            readOnly={readOnly}
            onCommit={(v) => {
              patchParams({ netRatio: clamp(v, 0, 1) })
            }}
          />
          <NumberField
            label="Pensions-Quote"
            value={params.pensionRatio}
            decimals
            readOnly={readOnly}
            onCommit={(v) => {
              patchParams({ pensionRatio: clamp(v, 0, 1) })
            }}
          />
        </ParamsPanel>

        <BesoldungstafelPanel />
      </div>
    </div>
  )
}

// === Bausteine ==============================================================

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4">
      <p className="mb-3 text-sm font-medium text-text-primary">{title}</p>
      <div className="space-y-1">{children}</div>
    </section>
  )
}

function ResultRow({
  label,
  cents,
  text,
  strong,
  tone,
  suffix,
}: {
  label: string
  cents?: number
  text?: string
  strong?: boolean
  tone?: 'warning' | 'danger'
  suffix?: string
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span
        className={cn(
          'min-w-0 flex-1 text-sm',
          strong ? 'font-medium text-text-primary' : 'text-text-secondary',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'shrink-0 text-right font-mono text-sm tabular-nums',
          tone === 'warning' && 'text-warning',
          tone === 'danger' && 'text-danger',
          !tone && (strong ? 'font-medium text-text-primary' : 'text-text-primary'),
        )}
      >
        {text ?? formatCurrency(cents ?? 0)}
        {suffix}
      </span>
    </div>
  )
}

/** Lücken-Zeile: Betrag als Badge — grün bei ≥ 0, rot bei Lücke. */
function GapRow({ label, cents }: { label: string; cents: number }) {
  const positive = cents >= 0
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="min-w-0 flex-1 text-sm font-medium text-text-primary">{label}</span>
      <span
        className={cn(
          'inline-flex shrink-0 items-center rounded px-2 py-0.5 font-mono text-sm tabular-nums',
          positive ? 'bg-success/15 text-success' : 'bg-danger/15 text-danger',
        )}
      >
        {formatCurrency(cents)}
      </span>
    </div>
  )
}

function EuroField({
  label,
  cents,
  maxCents = FINANCE_MAX_MONTHLY_CENTS,
  readOnly,
  onCommit,
}: {
  label: string
  cents: number
  maxCents?: number
  readOnly: boolean
  onCommit: (cents: number) => void
}) {
  const id = useId()
  const [value, setValue] = useState(centsToInput(cents))
  const focused = useRef(false)
  useEffect(() => {
    if (!focused.current) setValue(centsToInput(cents))
  }, [cents])

  if (readOnly) {
    return (
      <div className="space-y-1.5">
        <span className="text-xs text-text-secondary">{label}</span>
        <p className="font-mono text-sm tabular-nums text-text-primary">{formatCurrency(cents)}</p>
      </div>
    )
  }
  return (
    <div className="block space-y-1.5">
      <label htmlFor={id} className="text-xs text-text-secondary">
        {label}
      </label>
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        placeholder="0,00"
        value={value}
        onFocus={() => {
          focused.current = true
        }}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          focused.current = false
          const parsed = parseCents(value)
          if (parsed === null) {
            setValue(centsToInput(cents))
            return
          }
          // Auf die geteilte Server-Grenze clampen (sonst unverständlicher 400).
          const bounded = Math.min(parsed, maxCents)
          if (bounded !== cents) onCommit(bounded)
          // Anzeige normalisieren (z.B. '2.500' → '2500,00'), auch ohne Änderung.
          else setValue(centsToInput(cents))
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
        }}
        className="h-9 text-right text-sm tabular-nums"
      />
    </div>
  )
}

function NumberField({
  label,
  value,
  decimals,
  allowNegative,
  readOnly,
  onCommit,
}: {
  label: string
  value: number
  decimals?: boolean
  allowNegative?: boolean
  readOnly: boolean
  onCommit: (value: number) => void
}) {
  const id = useId()
  // Float-Rauschen aus Rate↔Prozent-Umrechnungen wegrunden ('0,9' würde
  // sonst als '0,9000000000000001' zurückkommen — Review-Fund).
  const clean = Math.round(value * 1e6) / 1e6
  const display = decimals ? String(clean).replace('.', ',') : String(clean)
  const [raw, setRaw] = useState(display)
  const focused = useRef(false)
  useEffect(() => {
    if (!focused.current) setRaw(decimals ? String(clean).replace('.', ',') : String(clean))
  }, [clean, decimals])

  if (readOnly) {
    return (
      <div className="space-y-1.5">
        <span className="text-xs text-text-secondary">{label}</span>
        <p className="font-mono text-sm tabular-nums text-text-primary">{display}</p>
      </div>
    )
  }
  return (
    <div className="block space-y-1.5">
      <label htmlFor={id} className="text-xs text-text-secondary">
        {label}
      </label>
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        value={raw}
        onFocus={() => {
          focused.current = true
        }}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() => {
          focused.current = false
          const parsed = parseDecimalInput(raw, clean)
          const bounded = allowNegative ? parsed : Math.max(parsed, 0)
          if (bounded !== clean) onCommit(bounded)
          else setRaw(decimals ? String(clean).replace('.', ',') : String(clean))
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
        }}
        className="h-9 text-right text-sm tabular-nums"
      />
    </div>
  )
}

function ParamsPanel({ legend, children }: { legend: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-sm font-medium text-text-primary"
      >
        {legend}
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && <div className="mt-3 space-y-3">{children}</div>}
    </section>
  )
}

function BesoldungstafelPanel() {
  const [open, setOpen] = useState(false)
  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-sm font-medium text-text-primary"
      >
        Besoldungstafel (Stand 2021)
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-2xs uppercase tracking-wider text-text-tertiary">
                <th className="py-1 pr-2 font-medium">Gruppe</th>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => (
                  <th key={s} className="px-1 py-1 text-right font-medium">
                    {s}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {BESOLDUNGSTAFEL_2021.map((row) => (
                <tr key={row.group}>
                  <td className="py-1 pr-2 font-medium text-text-primary">{row.group}</td>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((s) => {
                    const v = besoldungFor(row.group, s)
                    return (
                      <td
                        key={s}
                        className="px-1 py-1 text-right font-mono tabular-nums text-text-secondary"
                      >
                        {v === null ? '—' : formatNumber(v / 100, 0)}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function GapDonut({
  title,
  slices,
  empty,
}: {
  title: string
  slices: Array<{ name: string; value: number; color: string }>
  empty: boolean
}) {
  const colors = useThemeColors()
  const filtered = slices.filter((s) => s.value > 0)
  const total = filtered.reduce((a, s) => a + s.value, 0)
  return (
    <section className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-4">
      <p className="text-sm font-medium text-text-primary">{title}</p>
      {empty || total <= 0 ? (
        <p className="mt-3 text-sm text-text-tertiary">Noch keine Daten eingetragen.</p>
      ) : (
        <>
          <div className="mt-1 h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={filtered}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="45%"
                  outerRadius="80%"
                  paddingAngle={2}
                  isAnimationActive={false}
                  stroke="none"
                >
                  {filtered.map((s) => (
                    <Cell key={s.name} fill={s.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: colors.tooltipBg,
                    border: `1px solid ${colors.tooltipBorder}`,
                    borderRadius: 8,
                    fontSize: 11,
                  }}
                  labelStyle={{ color: colors.textPrimary }}
                  formatter={(v) => [formatCurrency(Number(v)), '']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1">
            {filtered.map((s) => (
              <div key={s.name} className="flex items-center gap-2 text-xs">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="min-w-0 flex-1 truncate text-text-secondary">{s.name}</span>
                <span className="w-24 text-right font-mono tabular-nums text-text-primary">
                  {formatCurrency(s.value)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}
